import { Button } from "@/components/core/buttons/button";
import { Form, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form.tsx";
import { Input } from "@/components/core/forms/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/core/tabs";
import { CancelInvoiceModal } from "@/components/invoices/cancel-invoice-modal";
import { JournalItemsTab } from "@/components/invoices/journal-items-tab";
import { OtherInfoTab } from "@/components/invoices/other-info-tab";
import { Content } from "@/components/layouts/content";
import { invoiceQueryOptions, useInvoiceCreateMutation, useInvoicePreviewMutation } from "@/features/dashboard/api/queries/invoice-query";
import { type Invoice, invoiceSchema } from "@/features/dashboard/types/invoice.ts";
import { downloadFile } from "@/utils/download-file";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconCirclePlus, IconTrash } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { toast } from "sonner";
import { v4 as uuid } from "uuid";

export const Route = createFileRoute("/_dashboard/invoices_/$invoiceId")({
	component: DashboardIndex,
});

function DashboardIndex() {
	const params = Route.useParams();
	const invoiceId = params.invoiceId;

	const query = useSuspenseQuery(invoiceQueryOptions(invoiceId));
	const data = query.data;
	const invoiceMutation = useInvoiceCreateMutation();
	const invoicePreviewMutation = useInvoicePreviewMutation();
	const [isDownloading, setIsDownloading] = useState(false);
	const [cancelModalOpen, setCancelModalOpen] = useState(false);

	const isCancelled = Boolean(data.cancelled_at);
	const canCancel = !isCancelled && (data.status === "pending" || data.status === "paid");
	const hasCollection = Boolean(data.collection_status === "collected" || data.amount_collected);

	const form = useForm<Invoice>({
		resolver: zodResolver(invoiceSchema),
		defaultValues: {
			id: data.id || null,
			sold_to: data.sold_to || "",
			tin_number: data.tin_number || "",
			address: data.address || "",
			reference_number: data.reference_number || "",
			invoice_number: data.invoice_number || "",
			terms: data.terms || "",
			order_reference_number: data.order_reference_number || "",
			prepared_by: data.prepared_by || "",
			approved_by: data.approved_by || "",
			vatable_sales: data.vatable_sales || null,
			vat_exempt: data.vat_exempt || null,
			zero_rated: data.zero_rated || null,
			total_sales: data.total_sales || null,
			withholding_tax: data.withholding_tax || null,
			vat: data.vat || null,
			total_amount_due: data.total_amount_due || null,
			invoice_date: data.invoice_date || new Date().toISOString().split("T")[0],
			items:
				data.items && data.items.length > 0
					? data.items.map((item) => ({
							id: item.id || uuid(),
							quantity: item.quantity || "1",
							unit_of_measurement: item.unit_of_measurement || "",
							item_description: item.item_description || "",
							unit_price: item.unit_price || "",
							total_price: item.total_price || "",
						}))
					: [
							{
								id: uuid(),
								quantity: "1",
								unit_of_measurement: "",
								item_description: "",
								unit_price: "",
								total_price: "",
							},
						],
		},
	});

	const items = useFieldArray({
		control: form.control,
		name: "items",
	});

	const itemWatch = form.watch("items");

	useEffect(() => {
		if (itemWatch !== undefined && itemWatch.length > 0) {
			const totalAmountDue = itemWatch.reduce((acc, item) => {
				const quantity = Number.parseFloat(item.quantity) || 0;
				const unitPrice = Number.parseFloat(item.unit_price) || 0;
				const totalPrice = quantity * unitPrice;
				return acc + totalPrice;
			}, 0);
			form.setValue("total_amount_due", totalAmountDue);
			const vatableSales = totalAmountDue / 1.12;
			form.setValue("vatable_sales", vatableSales);
			const wTax = vatableSales * 0.02;
			form.setValue("withholding_tax", wTax);
			const vat = vatableSales * 0.12;
			form.setValue("vat", vat);
		}
	}, [JSON.stringify(itemWatch)]);

	const handleAddRow = () => {
		items.append({
			id: uuid(),
			quantity: "1",
			unit_of_measurement: "",
			item_description: "",
			unit_price: "",
			total_price: "",
		});
	};

	const handleSubmit = async (values: Invoice) => {
		const response = await invoiceMutation.mutateAsync(values);
		handleDownload(response.id);
	};

	const handleDownload = async (invoiceId: string | null | undefined) => {
		setIsDownloading(true);
		toast("Donwloading...");
		const response = await invoicePreviewMutation.mutateAsync({
			invoice_id: invoiceId,
		});

		const url = URL.createObjectURL(response);

		downloadFile(url, "invoice.pdf");
		setIsDownloading(false);
	};

	return (
		<Content>
			<div className="flex flex-col w-full p-4">
				<Form {...form}>
					<form className="flex flex-col w-full gap-6" onSubmit={form.handleSubmit(handleSubmit)}>
						<div className="flex flex-col w-full gap-6">
							<div className="flex flex-row items-center justify-between w-full gap-4">
								<div className="inline-flex flex-col w-auto">
									<span className="text-xl text-foreground-dark font-semibold">
										Invoice
										{isCancelled && (
											<span className="ml-3 inline-block rounded-full bg-red-100 border border-red-200 px-2 py-0.5 text-xs font-medium text-red-800 align-middle">
												CANCELLED
											</span>
										)}
									</span>
									<span className="text-base text-foreground/70">
										{isCancelled ? `Cancelled: ${data.cancellation_reason ?? "no reason recorded"}` : "Fill up details of invoice below."}
									</span>
								</div>
								{canCancel && (
									<Button type="button" variant="danger" onClick={() => setCancelModalOpen(true)}>
										Cancel invoice
									</Button>
								)}
							</div>
							<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
								<div className="col-span-4">
									<FormField
										control={form.control}
										name="invoice_number"
										render={({ field }) => (
											<FormItem>
												<FormLabel required>Invoice No. (for record purposes only)</FormLabel>
												<FormGroup>
													<Input className="input" placeholder="" {...field} />
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
							</div>
							<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
								<div className="col-span-4">
									<FormField
										control={form.control}
										name="sold_to"
										render={({ field }) => (
											<FormItem>
												<FormLabel required>Sold To:</FormLabel>
												<FormGroup>
													<Input className="input" placeholder="" {...field} />
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
								<div className="col-span-4" />
								<div className="col-span-4">
									<FormField
										control={form.control}
										name="reference_number"
										render={({ field }) => (
											<FormItem>
												<FormLabel required>Reference No.:</FormLabel>
												<FormGroup>
													<Input className="input" placeholder="" {...field} />
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>

								<div className="col-span-4">
									<FormField
										control={form.control}
										name="tin_number"
										render={({ field }) => (
											<FormItem>
												<FormLabel required>TIN No.:</FormLabel>
												<FormGroup>
													<Input className="input" placeholder="" {...field} />
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
								<div className="col-span-4" />
								<div className="col-span-4">
									<FormField
										control={form.control}
										name="invoice_date"
										render={({ field }) => (
											<FormItem>
												<FormLabel required>Invoice Date:</FormLabel>
												<FormGroup>
													<Input className="input" type="date" placeholder="" {...field} />
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>

								<div className="col-span-8">
									<FormField
										control={form.control}
										name="address"
										render={({ field }) => (
											<FormItem>
												<FormLabel required>Address:</FormLabel>
												<FormGroup>
													<Input className="input" placeholder="" {...field} />
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
								<div className="col-span-4">
									<FormField
										control={form.control}
										name="terms"
										render={({ field }) => (
											<FormItem>
												<FormLabel required>Terms:</FormLabel>
												<FormGroup>
													<Input className="input" placeholder="" {...field} />
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>

								<div className="col-span-8" />
								<div className="col-span-4">
									<FormField
										control={form.control}
										name="order_reference_number"
										render={({ field }) => (
											<FormItem>
												<FormLabel required>Order Reference No.:</FormLabel>
												<FormGroup>
													<Input className="input" placeholder="" {...field} />
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
							</div>

							<Tabs defaultValue="lines" variant="compact" className="mt-4">
								<TabsList>
									<TabsTrigger value="lines">Invoice Lines</TabsTrigger>
									<TabsTrigger value="journal">Journal Items</TabsTrigger>
									<TabsTrigger value="other">Other Info</TabsTrigger>
								</TabsList>
								<TabsContent value="lines" className="pt-4">
									<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3 ">
										<Button type="button" onClick={handleAddRow} size="default" variant="ghost">
											<IconCirclePlus size={18} stroke={2} /> Add New Item
										</Button>
										<div className="col-span-12">
											<table className="w-full text-sm border">
												<thead>
													<tr>
														<th className="text-left">Quantity</th>
														<th className="text-left">Unit of Measurement</th>
														<th className="text-left">Item Description</th>
														<th className="text-left">Unit Price</th>
														<th className="text-left">Total Price</th>
														<th className="text-left">Actions</th>
													</tr>
												</thead>
												<tbody>
													{items.fields.map((item, index) => (
														<tr key={item.id}>
															<td width={"10%"}>
																<FormField
																	control={form.control}
																	name={`items.${index}.quantity`}
																	render={({ field }) => (
																		<FormItem>
																			<FormGroup>
																				<Input className="input" type="number" placeholder="Quantity" {...field} />
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</td>
															<td width={"10%"}>
																<FormField
																	control={form.control}
																	name={`items.${index}.unit_of_measurement`}
																	render={({ field }) => (
																		<FormItem>
																			<FormGroup>
																				<Input className="input" placeholder="Unit of Measurement" {...field} />
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</td>
															<td width={"55%"}>
																<FormField
																	control={form.control}
																	name={`items.${index}.item_description`}
																	render={({ field }) => (
																		<FormItem>
																			<FormGroup>
																				<Input className="input" placeholder="Item Description" {...field} />
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</td>
															<td width={"10%"}>
																<FormField
																	control={form.control}
																	name={`items.${index}.unit_price`}
																	render={({ field }) => (
																		<FormItem>
																			<FormGroup>
																				<Input className="input" type="number" placeholder="Unit Price" {...field} />
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</td>
															<td width={"10%"}>
																<FormField
																	control={form.control}
																	name={`items.${index}.total_price`}
																	render={({ field }) => {
																		const quantity = Number(form.watch(`items.${index}.quantity`) || 0);
																		const unitPrice = Number(form.watch(`items.${index}.unit_price`) || 0);
																		const total = quantity * unitPrice;
																		if (field.value !== String(total)) {
																			form.setValue(`items.${index}.total_price`, String(total), { shouldValidate: false });
																		}
																		return (
																			<FormItem>
																				<FormGroup>
																					<Input className="input" type="number" placeholder="Total Price" {...field} value={total} readOnly tabIndex={-1} />
																				</FormGroup>
																				<FormMessage />
																			</FormItem>
																		);
																	}}
																/>
															</td>
															<td width={"5%"} className="flex gap-2">
																<Button className="mt-1" variant="danger" size="sm" onClick={() => items.remove(index)}>
																	<IconTrash size={16} />
																</Button>
															</td>
														</tr>
													))}
												</tbody>
												<tfoot>
													<tr>
														<td colSpan={3} />
														<td>VATABLE SALES</td>
														<td>
															<FormField
																control={form.control}
																name="vatable_sales"
																render={({ field }) => (
																	<FormItem>
																		<FormGroup>
																			<Input className="input" type="number" placeholder="Vatable Sales" {...field} />
																		</FormGroup>
																		<FormMessage />
																	</FormItem>
																)}
															/>
														</td>
													</tr>
													<tr>
														<td colSpan={3} />
														<td>VAT-EXEMPT SALES</td>
														<td>
															<FormField
																control={form.control}
																name="vat_exempt"
																render={({ field }) => (
																	<FormItem>
																		<FormGroup>
																			<Input className="input" type="number" placeholder="VAT-EXEMPT SALES" {...field} />
																		</FormGroup>
																		<FormMessage />
																	</FormItem>
																)}
															/>
														</td>
													</tr>
													<tr>
														<td colSpan={3} />
														<td>ZERO-RATED SALES</td>
														<td>
															<FormField
																control={form.control}
																name="zero_rated"
																render={({ field }) => (
																	<FormItem>
																		<FormGroup>
																			<Input className="input" type="number" placeholder="ZERO-RATED SALES" {...field} />
																		</FormGroup>
																		<FormMessage />
																	</FormItem>
																)}
															/>
														</td>
													</tr>
													<tr>
														<td colSpan={3} />
														<td>TOTAL SALES</td>
														<td>
															<FormField
																control={form.control}
																name="total_sales"
																render={({ field }) => (
																	<FormItem>
																		<FormGroup>
																			<Input className="input" type="number" placeholder="TOTAL SALES" {...field} />
																		</FormGroup>
																		<FormMessage />
																	</FormItem>
																)}
															/>
														</td>
													</tr>
													<tr>
														<td colSpan={3} />
														<td>WITHHOLDING TAX</td>
														<td>
															<FormField
																control={form.control}
																name="withholding_tax"
																render={({ field }) => (
																	<FormItem>
																		<FormGroup>
																			<Input className="input" type="number" placeholder="WITHHOLDING TAX" {...field} />
																		</FormGroup>
																		<FormMessage />
																	</FormItem>
																)}
															/>
														</td>
													</tr>
													<tr>
														<td colSpan={3} />
														<td>VALUE ADDED TAX</td>
														<td>
															<FormField
																control={form.control}
																name="vat"
																render={({ field }) => (
																	<FormItem>
																		<FormGroup>
																			<Input className="input" type="number" placeholder="VALUE ADDED TAX" {...field} />
																		</FormGroup>
																		<FormMessage />
																	</FormItem>
																)}
															/>
														</td>
													</tr>
													<tr>
														<td colSpan={3} />
														<td>TOTAL AMOUNT DUE</td>
														<td>
															<FormField
																control={form.control}
																name="total_amount_due"
																render={({ field }) => (
																	<FormItem>
																		<FormGroup>
																			<Input className="input" type="number" placeholder="TOTAL AMOUNT DUE" {...field} />
																		</FormGroup>
																		<FormMessage />
																	</FormItem>
																)}
															/>
														</td>
													</tr>
												</tfoot>
											</table>
										</div>
									</div>
								</TabsContent>
								<TabsContent value="journal" className="pt-4">
									{invoiceId ? <JournalItemsTab invoiceId={invoiceId} /> : null}
								</TabsContent>
								<TabsContent value="other" className="pt-4">
									<OtherInfoTab invoice={data} />
								</TabsContent>
							</Tabs>

							<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3 mt-4">
								<div className="col-span-4">
									<FormField
										control={form.control}
										name="prepared_by"
										render={({ field }) => (
											<FormItem>
												<FormLabel required>Prepared By:</FormLabel>
												<FormGroup>
													<Input className="input" placeholder="" {...field} />
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
								<div className="col-span-4" />
								<div className="col-span-4">
									<FormField
										control={form.control}
										name="approved_by"
										render={({ field }) => (
											<FormItem>
												<FormLabel required>Approved By:</FormLabel>
												<FormGroup>
													<Input className="input" placeholder="" {...field} />
												</FormGroup>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
							</div>
						</div>
						<div className="col-span-12 flex gap-2">
							<Button type="button" variant={"ghost"}>
								<Link to="/">Cancel</Link>
							</Button>
							<Button type="submit" disabled={isDownloading}>
								Save & Download
							</Button>
						</div>
					</form>
				</Form>
				<CancelInvoiceModal
					isOpen={cancelModalOpen}
					onClose={() => setCancelModalOpen(false)}
					invoiceId={invoiceId}
					invoiceNumber={data.invoice_number ?? invoiceId}
					hasCollection={hasCollection}
				/>
			</div>
		</Content>
	);
}
